import { useTranslation } from "react-i18next";
import { Check, Circle, Link2, MousePointer2, MoveUpRight, Pencil, Square, Type } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { ScribbleUnderline, StickerBadge } from "../ui/decorations";

/**
 * Seção exclusiva dos Quadros (lousa infinita integrada) — diferencial do
 * Taskletto. Bloco ink sólido com glow laranja (assinatura de seções escuras)
 * e um mock de canvas onde o diagrama "se desenha" ao entrar na viewport,
 * usando o sistema .scribble-path existente (CSS puro + IntersectionObserver,
 * já com prefers-reduced-motion tratado no index.css).
 */
export function DrawingsShowcase() {
  const { t } = useTranslation("landing");
  return (
    <section id="quadros" className="relative py-24 md:py-32 bg-ink overflow-hidden">
      {/* Glow que respira */}
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] max-w-[90vw] rounded-full bg-primary-600/25 blur-[130px] pointer-events-none animate-glow-breathe" />
      {/* Textura de pontos */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        {/* Texto */}
        <Reveal>
          <StickerBadge className="border-ink bg-primary-500 text-ink mb-6" rotate={-3}>
            <Pencil size={11} />
            {t("drawings.badge")}
          </StickerBadge>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight text-balance leading-[1.1]">
            {t("drawings.titlePrefix")}{" "}
            <ScribbleUnderline strokeClassName="text-primary-500">{t("drawings.titleHighlight")}</ScribbleUnderline>{t("drawings.titleSuffix")}
          </h2>
          <p className="mt-6 text-white/80 text-sm sm:text-base leading-relaxed max-w-md">
            {t("drawings.subtitle")}
          </p>
          <ul className="mt-8 space-y-3.5">
            {[
              t("drawings.feat1"),
              t("drawings.feat2"),
              t("drawings.feat3"),
            ].map((f) => (
              <li key={f} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <Check size={12} className="text-primary-400" strokeWidth={3} />
                </span>
                <span className="text-sm text-white/80">{f}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-semibold text-primary-200/70">
            {t("drawings.footnote")}
          </p>
        </Reveal>

        {/* Mock do canvas: o diagrama se desenha ao entrar na viewport */}
        <Reveal delay={120}>
          <div className="relative">
            <div className="relative rounded-3xl bg-white border-2 border-ink/10 shadow-2xl overflow-hidden -rotate-1 transition-transform duration-500 hover:rotate-0">
              {/* Grade de pontos do canvas */}
              <div
                className="absolute inset-0 opacity-[0.35] pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(circle, rgba(45,20,5,0.22) 1px, transparent 1px)",
                  backgroundSize: "22px 22px",
                }}
              />

              {/* Toolbar flutuante (mock) */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-xl bg-white border border-ink/10 shadow-lg px-1.5 py-1">
                <span className="p-1.5 rounded-lg text-ink/45"><MousePointer2 size={13} /></span>
                <span className="p-1.5 rounded-lg bg-primary-500 text-ink"><Square size={13} /></span>
                <span className="p-1.5 rounded-lg text-ink/45"><Circle size={13} /></span>
                <span className="p-1.5 rounded-lg text-ink/45"><MoveUpRight size={13} /></span>
                <span className="p-1.5 rounded-lg text-ink/45"><Type size={13} /></span>
                <span className="p-1.5 rounded-lg text-ink/45"><Pencil size={13} /></span>
              </div>

              {/* Diagrama à mão que se desenha (stagger via animation-delay inline) */}
              <svg viewBox="0 0 480 320" fill="none" className="relative w-full" aria-hidden="true">
                {/* caixa "ideia" */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  d="M42 74 C 80 70, 130 71, 158 74 C 161 90, 160 108, 157 122 C 120 126, 76 125, 44 121 C 40 106, 40 88, 42 74 Z"
                  stroke="#2d1405" strokeWidth="2.5" strokeLinecap="round"
                />
                {/* seta ideia -> rascunho */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "0.75s" }}
                  d="M162 120 C 200 150, 210 165, 232 182 M232 182 l -16 -4 M232 182 l 2 -16"
                  stroke="#ea6e29" strokeWidth="2.5" strokeLinecap="round"
                />
                {/* caixa "rascunho" */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "1.1s" }}
                  d="M238 186 C 280 182, 330 183, 360 186 C 364 203, 363 222, 360 237 C 320 241, 272 240, 240 236 C 236 220, 236 201, 238 186 Z"
                  stroke="#2d1405" strokeWidth="2.5" strokeLinecap="round"
                />
                {/* seta rascunho -> uau */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "1.55s" }}
                  d="M362 182 C 390 150, 396 132, 404 112 M404 112 l -14 6 M404 112 l 4 15"
                  stroke="#ea6e29" strokeWidth="2.5" strokeLinecap="round"
                />
                {/* elipse "uau!" */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "1.9s" }}
                  d="M396 78 C 402 62, 428 56, 446 62 C 462 68, 466 86, 452 96 C 436 106, 406 104, 398 92 C 394 86, 394 82, 396 78"
                  stroke="#ff8d48" strokeWidth="3" strokeLinecap="round"
                />
                {/* raiozinhos de destaque */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "2.3s" }}
                  d="M448 40 l 6 -12 M466 52 l 12 -7 M472 74 l 14 0"
                  stroke="#ff8d48" strokeWidth="2.5" strokeLinecap="round"
                />
                {/* rabisco solto no canto */}
                <path
                  className="scribble-path"
                  pathLength={1}
                  style={{ animationDelay: "2.5s" }}
                  d="M56 250 C 70 238, 84 258, 98 246 C 112 234, 124 256, 140 244"
                  stroke="#2d1405" strokeWidth="2.5" strokeLinecap="round" opacity="0.35"
                />
                {/* rótulos */}
                <text x="72" y="104" fill="#2d1405" fontSize="15" fontWeight="700" fontFamily="Inter, sans-serif">{t("drawings.labelIdea")}</text>
                <text x="258" y="217" fill="#2d1405" fontSize="15" fontWeight="700" fontFamily="Inter, sans-serif">{t("drawings.labelSketch")}</text>
                <text x="408" y="85" fill="#ea6e29" fontSize="14" fontWeight="800" fontFamily="Inter, sans-serif">{t("drawings.labelWow")}</text>
              </svg>

              {/* Cursor de quem desenha */}
              <div className="absolute bottom-16 right-24 animate-float-slower pointer-events-none">
                <MousePointer2 size={16} className="text-primary-600 fill-primary-500" />
                <span className="ml-3 -mt-1 block rounded-full bg-primary-600 text-white text-[9px] font-bold px-2 py-0.5 w-max">
                  {t("drawings.cursorYou")}
                </span>
              </div>
            </div>

            {/* Sticker de autosave flutuando sobre o canvas */}
            <div className="absolute -bottom-5 -left-3 sm:-left-6 animate-float-slow" style={{ "--float-rotate": "3deg" } as React.CSSProperties}>
              <StickerBadge className="border-ink/15 bg-white text-ink shadow-lg" rotate={3}>
                <Check size={11} className="text-primary-600" strokeWidth={3} />
                {t("drawings.stickerSaved")}
              </StickerBadge>
            </div>
            {/* Sticker de compartilhamento */}
            <div className="absolute -top-4 -right-2 sm:-right-5 animate-float-slow" style={{ "--float-rotate": "-2deg" } as React.CSSProperties}>
              <StickerBadge className="border-ink bg-primary-500 text-ink shadow-lg" rotate={-4}>
                <Link2 size={11} />
                {t("drawings.stickerShared")}
              </StickerBadge>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
